import type { KeyboardEvent, ReactNode } from "react"
import type { LucideIcon } from "lucide-react"

import { cn } from "../../lib/utils"

// The one segmented-control primitive for mutually-exclusive toggles across the
// app (feed view mode, OK/NOK verdict filter, dashboard period). One connected
// pill: a bordered wrapper of borderless buttons, the active one filled with a
// neutral `muted` tint. The neutral fill (rather than a primary fill) keeps any
// per-segment icon tint — e.g. the green OK check, the red NOK triangle —
// readable on the active segment. Leading icons + a trailing count are optional.
// `size` picks the vertical rhythm: `sm` for dense toolbars (default), `md` for
// the fixed-height (`h-10`) variant that lines up with a standard Button.
export type SegmentedOption<T extends string> = {
  value: T
  label: ReactNode
  icon?: LucideIcon
  /** Persistent icon tint, e.g. `text-success` / `text-destructive`, kept
   *  regardless of active state. Omit to let the icon follow the label color. */
  iconClassName?: string
  /** Trailing count rendered muted after the label, e.g. "All 930". */
  count?: number
}

type SegmentedSize = "sm" | "md"

// `md` pins the wrapper height so the control matches an adjacent `h-10` Button
// exactly; `items-stretch` (below) then lets the active fill span that height.
const WRAPPER_SIZE: Record<SegmentedSize, string> = {
  sm: "text-xs",
  md: "h-9 text-[13px]",
}

const BUTTON_SIZE: Record<SegmentedSize, string> = {
  sm: "px-3 py-1",
  md: "px-3.5",
}

type SegmentedControlProps<T extends string> = {
  options: readonly SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  size?: SegmentedSize
  disabled?: boolean
  /** Names the control for assistive tech (the wrapper is a button group). */
  label?: string
  className?: string
  /** Tab semantics for mounted panels. The caller owns selection and keyboard navigation. */
  tabs?: {
    idPrefix: string
    onKeyDown: (event: KeyboardEvent<HTMLButtonElement>, value: T) => void
  }
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = "sm",
  disabled = false,
  label,
  className,
  tabs,
}: SegmentedControlProps<T>) {
  return (
    <div
      role={tabs ? "tablist" : "group"}
      aria-label={label}
      className={cn(
        "inline-flex items-stretch gap-0.5 rounded-full bg-muted p-0.5",
        WRAPPER_SIZE[size],
        className
      )}
    >
      {options.map((option) => {
        const Icon = option.icon
        const active = value === option.value
        return (
          <button
            key={option.value}
            type="button"
            role={tabs ? "tab" : undefined}
            id={tabs ? `${tabs.idPrefix}-${option.value}-tab` : undefined}
            aria-controls={tabs ? `${tabs.idPrefix}-${option.value}-panel` : undefined}
            aria-selected={tabs ? active : undefined}
            aria-pressed={tabs ? undefined : active}
            tabIndex={tabs && !active ? -1 : undefined}
            onKeyDown={tabs ? (event) => tabs.onKeyDown(event, option.value) : undefined}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "inline-flex min-w-0 items-center justify-center gap-1.5 rounded-full font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring transition-[color,background-color,transform] duration-200 ease-[var(--ease-product-out)] active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50",
              BUTTON_SIZE[size],
              active
                ? "bg-card text-foreground shadow-card ring-1 ring-border"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {Icon && <Icon className={cn("size-3.5 shrink-0", option.iconClassName)} />}

            {option.label}

            {option.count !== undefined && (
              <span className="tabular-nums text-muted-foreground">{option.count}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}
